"use client";

import BFLModal from "@repo/shared-ui/BFLModal";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import Conditional from "@repo/shared-ui/Conditional";
import useModalCloseOnBrowserButton from "@repo/shared-ui/useModalCloseOnBrowserButton";
import { getSessionData } from "@repo/shared-utils/common-utils";
import {
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  selectLoginStatus,
  useAppSelector,
  useAppDispatch,
  setLeadStatus,
  LeadStatus,
} from "@repo/web-store";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { ComponentProps, ComponentType, FC, Suspense, useEffect, useMemo, useState } from "react";
import { consentInsert, consentRead } from "@repo/sso/consentApi";
import { getHoldings } from "../../services/TradingMallService";
import { handleRedirection } from "../../utils/helper";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import {
  handleClickDL,
  handleDematAccountDL,
  handleViewDL,
} from "../Demat/DematAccountFunction";
import OpenDematButton from "../Demat/DematButton";
import DematConsentModal from "../Demat/DematConsentModal";
import { DematOtpSendModalProps } from "../Demat/DematOtpSendModal";
import { redirectWithUtmParams } from "../Demat/DematUtils";
import FallbackComponent from "./components/FallbackComponent";
import PortfolioLockedCard from "./PortfolioLockedCard";
import {
  CmpDataPortfolioProps,
  HoldingsResponse,
  PortfolioSummary,
} from "./PortfolioTypes";

const DematOtpSendModal: ComponentType<DematOtpSendModalProps> = dynamic(
  () => import("../Demat/DematOtpSendModal.tsx").then((m) => m.default),
  { ssr: false, loading: () => <BFLSkeleton count={1} height={100} /> },
);
const DematOtpVerifyModal: ComponentType<DematOtpVerifyModalProps> = dynamic(
  () => import("../Demat/DematOtpVerifyModal.tsx") as any,
  { ssr: false, loading: () => <BFLSkeleton count={1} height={100} /> },
);

interface DematOtpVerifyModalProps {
  onValidationSuccess?: (otpValue: string) => void;
  onValidationFailure?: (otpValue: string) => void;
  mobileNumber?: string;
}

interface PortfolioCardRenderProps {
  cmpData: CmpDataPortfolioProps;
}

const DEFAULT_SUMMARY: PortfolioSummary = {
  totalCurrentValue: "0",
  totalInvestedValue: "0",
  totalPL: "0",
  totalPLPercentage: "0.00",
  total1DayPL: "0",
  total1DayPLPercentage: "0.00",
  plStatus: "neutral",
  oneDayPLStatus: "neutral",
};

const PortfolioCardRender: FC<PortfolioCardRenderProps> = ({ cmpData }) => {
  const leadStatus = useAppSelector(selectLeadStatus);
  const isAuthSuccessOrPerpetual = useAppSelector(
    selectIsAuthSuccessOrPerpetual,
  );
  const loginStatus = useAppSelector(selectLoginStatus);
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const [holdings, setHoldings] = useState<HoldingsResponse | null>(null);
  const [hasResponse, setHasResponse] = useState(false);
  const [apiError, setApiError] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isLoadingLeadStatus, setIsLoadingLeadStatus] = useState<boolean>(false);
  const [consentProvided, setConsentProvided] = useState<boolean>(false);
  const [isCheckingConsent, setIsCheckingConsent] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<
    "consent" | "send" | "verify" | "none"
  >("none");

  useModalCloseOnBrowserButton(openModal !== "none", () =>
    setOpenModal("none"),
  );
  const [sessionMobileNo, setSessionMobileNo] = useState<string | undefined>(
    undefined,
  );
  const code = searchParams.get("code") ?? "";

  const hasDemat = leadStatus === "ETB";



  const hasPortfolio = useMemo(
    () => {
      const result = holdings?.holdings?.length && holdings?.holdings?.length > 0;
      return result;
    },
    [holdings],
  ) as boolean;

  const portfolioData = useMemo<PortfolioSummary>(
    () => {
      const result = holdings?.portfolioSummary ?? DEFAULT_SUMMARY;
      return result;
    },
    [holdings],
  );



  useEffect(() => {
    if (!code) {
      setHasResponse(false);
      setApiError(false);
      return;
    }

    (async () => {
      try {
        setIsLoadingMore(currentPage > 1);
        setApiError(false);
        const response = await getHoldings(code, currentPage, 5);

        if (!response) {
          setHasResponse(true);
          setApiError(true);
          setHoldings(null);
          return;
        }

        setHasResponse(true);
        if (response?.data) {
          if (currentPage === 1) {
            setHoldings(response.data);
          } else {
            setHoldings((prev) => {
              if (!prev) {
                return response.data;
              }
              const merged = {
                ...response.data,
                holdings: [...prev.holdings, ...response.data.holdings],
              };
              return merged;
            });
          }
        } else {
          setHoldings(null);
        }
      } catch (error) {
        setHasResponse(true);
        setApiError(true);
        setHoldings(null);
      } finally {
        setIsLoadingMore(false);
      }
    })();
  }, [code, currentPage]);

  const handleLoadMore = () => {
    if (!isLoadingMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchSessionMobileNumber = async (retryCount = 0) => {
      try {
        const { mobileNo } = await getSessionData();
        if (mobileNo) {
          if (isMounted) setSessionMobileNo(mobileNo);
        } else if (retryCount < 10 && isMounted) {
          timeoutId = setTimeout(
            () => fetchSessionMobileNumber(retryCount + 1),
            500,
          );
        }
      } catch (error) {
        if (retryCount < 10 && isMounted) {
          timeoutId = setTimeout(
            () => fetchSessionMobileNumber(retryCount + 1),
            500,
          );
        }
      }
    };

    fetchSessionMobileNumber();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!isAuthSuccessOrPerpetual) {
      return;
    }

    if (leadStatus !== null) {
      setIsLoadingLeadStatus(false);
      return;
    }

    const fetchLeadStatus = async () => {
      try {
        setIsLoadingLeadStatus(true);
        const { leadsBuySell } = await import("@repo/sso/leadsApi");
        const { mobileNo } = await getSessionData();
        if (!mobileNo) {
          setIsLoadingLeadStatus(false);
          return;
        }

        const leadsData = await leadsBuySell(mobileNo);
        if (leadsData?.statusCode === 90) {
          dispatch(
            setLeadStatus({
              leadStatus: leadsData.data?.flag as LeadStatus,
            })
          );
        } else {
          dispatch(setLeadStatus({ leadStatus: "" as LeadStatus }));
        }
      } catch (error) {
        dispatch(setLeadStatus({ leadStatus: "" as LeadStatus }));
      } finally {
        setIsLoadingLeadStatus(false);
      }
    };

    fetchLeadStatus();
  }, [isAuthSuccessOrPerpetual, leadStatus, dispatch]);

  useEffect(() => {
    if (!isAuthSuccessOrPerpetual || hasDemat) {
      return;
    }

    const getConsentReadData = async () => {
      try {
        setIsCheckingConsent(true);
        const consentReadData = await consentRead("BFSL", "34", "BFL Entity");
        if (consentReadData?.data?.consentValue === "2") {
          setConsentProvided(true);
        } else {
          setConsentProvided(false);
        }
      } catch (error) {
        setConsentProvided(false);
      } finally {
        setIsCheckingConsent(false);
      }
    };

    getConsentReadData();
  }, [isAuthSuccessOrPerpetual, hasDemat]);

  const isFallback = !hasDemat && !hasPortfolio && isAuthSuccessOrPerpetual && !isLoadingLeadStatus && leadStatus !== null;

  const submitConsentInsert = async () => {
    const consentResponse: any = await consentInsert(
      "BFSL",
      "BFL Entity",
      true,
    );
    return consentResponse;
  };

  const handleOtpValidationSuccess = (otpValue: string) => {
    setOpenModal("none");
  };

  const handleOtpValidationFailure = (otpValue: string) => {
  };

  const formatMobileForDisplay = (mobile: string) => {
    if (!mobile || mobile.length < 4) {
      return "";
    }
    return `${mobile.slice(0, 2)}xxxxxx${mobile.slice(-2)}`;
  };


  const noStockFoundComponent = useMemo(
    () => {
      const condition = (apiError && isAuthSuccessOrPerpetual) || (code === "" && !hasResponse && isAuthSuccessOrPerpetual);
      return (
        <StockListNotFound
          errorMessage={
            cmpData?.noStockFoundText ?? "Unable to fetch portfolio data"
          }
          stockNotFoundImgUrl={
            cmpData?.urls?.stockNotFoundImgUrl ?? "https://cms-assets.bajajfinserv.in/is/image/bajajfinance/no-stocks"
          }
        />
      );
    },
    [apiError, hasResponse, isAuthSuccessOrPerpetual, cmpData, code],
  );

  const fallbackComponent = useMemo(
    () => {
      const condition = hasResponse && !hasPortfolio && isAuthSuccessOrPerpetual;
      const buyStocksUrl = cmpData?.urls?.loggedInBuyStocksUrlWeb ?? "/investments/top-gainers";
      return (
        <FallbackComponent
          onClick={() => handleRedirection(buyStocksUrl)}
          condition={condition}
          imageUrl={cmpData?.urls?.stockNotFoundImgUrl ?? "https://cms-assets.bajajfinserv.in/is/image/bajajfinance/no-stocks"}
          title={
            cmpData?.messages?.loggedInBuyStocksText ?? cmpData?.loggedInBuyStocksText ??
            "You have no stocks in your portfolio"
          }
          buttonText={cmpData?.loggedInBuyStocksButtonText ?? "Buy Stocks"}
        />
      );
    },
    [hasPortfolio, isAuthSuccessOrPerpetual, cmpData, hasResponse],
  );

  if (isFallback) {
    return (
      <>
        <div className="flex flex-col items-center justify-center">
          <StockListNotFound
            errorMessage={
              cmpData?.messages?.loggedInOpenDematText ?? cmpData?.loggedInOpenDematText ??
              "You don't have a demat account"
            }
            stockNotFoundImgUrl={
              cmpData?.urls?.stockNotFoundImgUrl ??
              "https://cms-assets.bajajfinserv.in/is/image/bajajfinance/no-stocks"
            }
          />

          <OpenDematButton
            showArrow={false}
            openDematButtonText={
              cmpData?.loggedInOpenDematButtonText ?? "OPEN DEMAT ACCOUNT"
            }
            className="flex h-[42px] w-[200px] items-center justify-center font-medium"
            onClick={async () => {
              handleDematAccountDL(
                loginStatus,
                cmpData?.loggedInOpenDematButtonText ?? "OPEN DEMAT ACCOUNT",
                "Unknown Company",
              );

              if (!isAuthSuccessOrPerpetual) {
                redirectWithUtmParams(
                  cmpData?.urls?.loggedInOpenDematUrlWeb ??
                  "https://pwa.bajajfinservsecurities.in/?sso-module=bfsl&utm_source=widget&utm_medium=organic_bfl&utm_campaign=stock-and-trading",
                  "false",
                );
                return;
              }

              if (isCheckingConsent) {
                return;
              }

              if (!consentProvided) {
                try {
                  const consentReadData = await consentRead("BFSL", "34", "BFL Entity");
                  if (consentReadData?.data?.consentValue === "2") {
                    setConsentProvided(true);
                    setOpenModal("send");
                  } else {
                    setConsentProvided(false);
                    setOpenModal("consent");
                    handleViewDL("REDIRECTING TO BFSL");
                  }
                } catch (error) {
                  setConsentProvided(false);
                  setOpenModal("consent");
                  handleViewDL("REDIRECTING TO BFSL");
                }
              } else {
                setOpenModal("send");
              }
            }}
          />
        </div>
        <Conditional if={openModal === "consent"}>
          <BFLModal
            data-testid="bfl-modal"
            isOpen={true}
            onClose={() => {
              setOpenModal("none");
              handleClickDL("CLOSE", "REDIRECTING TO BFSL");
            }}
            title="Redirecting you to Bajaj Financial Securities Limited"
            secondaryButtonText="Skip"
            onSecondaryButtonClick={() => {
              setOpenModal("none");
              handleClickDL("Skip", "REDIRECTING TO BFSL");
              redirectWithUtmParams(
                "https://pwa.bajajfinservsecurities.in/?sso-module=bfsl",
                "false",
              );
            }}
            onPrimaryButtonClick={async () => {
              try {
                const consentResponse = await submitConsentInsert();

                if (
                  consentResponse.statusCode === 90 &&
                  consentResponse.status === "success"
                ) {
                  setConsentProvided(true);
                  if (consentResponse.data?.mobileNumber) {
                    setSessionMobileNo(consentResponse.data.mobileNumber);
                  }
                  setOpenModal("send");
                  handleClickDL("Continue", "REDIRECTING TO BFSL");
                } else {
                  setConsentProvided(false);
                }
              } catch (error) {
                setConsentProvided(false);
              }
            }}
            enableScroll={false}
            buttonText="Continue"
          >
            <DematConsentModal />
          </BFLModal>
        </Conditional>

        <Conditional if={openModal === "send"}>
          <BFLModal
            isOpen={true}
            onClose={() => {
              setOpenModal("none");
              handleClickDL("CLOSE", "OPEN YOUR FREE DEMAT ACCOUNT");
            }}
            title="Open your free Demat and trading account"
            enableScroll={false}
            subtitle="Kickstart your investment journey today"
          >
            <DematOtpSendModal
              setOpenModal={setOpenModal}
              prefillMobileNumber={sessionMobileNo}
            />
          </BFLModal>
        </Conditional>

        <Conditional if={openModal === "verify"}>
          <BFLModal
            isOpen={true}
            onClose={() => {
              setOpenModal("none");
              handleClickDL("CLOSE", "OTP VERIFICATION PAGE");
            }}
            title="Verification required"
            subtitle={
              sessionMobileNo
                ? `A one-time password has been sent to your mobile number +91 ${formatMobileForDisplay(sessionMobileNo)}`
                : "A one-time password has been sent to your registered mobile number"
            }
          >
            <DematOtpVerifyModal
              onValidationSuccess={handleOtpValidationSuccess}
              onValidationFailure={handleOtpValidationFailure}
              mobileNumber={sessionMobileNo}
            />
          </BFLModal>
        </Conditional>
      </>
    );
  }

  if (isAuthSuccessOrPerpetual && leadStatus === null) {
    return <BFLSkeleton count={5} height={45} width="100%" />;
  }

  if (apiError && isAuthSuccessOrPerpetual) {
    return noStockFoundComponent;
  }

  return (
    <>
      {(() => {
        if (!hasResponse && isAuthSuccessOrPerpetual && code) {
          return noStockFoundComponent;
        } else if (code && hasResponse && !hasPortfolio && isAuthSuccessOrPerpetual) {
          return fallbackComponent;
        } else {
          return (
            <PortfolioLockedCard
              cmpData={cmpData}
              portfolioData={portfolioData}
              holdings={holdings}
              hasDemat={hasDemat}
              hasPortfolio={hasPortfolio}
              onLoadMore={handleLoadMore}
              isLoadingMore={isLoadingMore}
            />
          );
        }
      })()}
    </>
  );
};

// useSearchParams() requires a Suspense boundary on statically prerendered routes.
const SuspendedPortfolioCardRender = (
  props: ComponentProps<typeof PortfolioCardRender>,
) => (
  <Suspense fallback={<BFLSkeleton count={5} height={45} width="100%" />}>
    <PortfolioCardRender {...props} />
  </Suspense>
);

export default SuspendedPortfolioCardRender;